// src/pages/pacs/GodownApplyForm.js

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { db, storage, auth } from "../../firebaseConfig";
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  runTransaction,
  getDocs,
  query,
  where,
  updateDoc,
  orderBy,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import toast, { Toaster } from "react-hot-toast";
import jsPDF from "jspdf";
import dayjs from "dayjs";

/* ================= MASTER DATA ================= */

const ALL_INDIA_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const FALLBACK_DISTRICTS_BY_STATE = {
  Gujarat: [
    "Ahmedabad",
    "Amreli",
    "Anand",
    "Aravalli",
    "Banaskantha",
    "Bharuch",
    "Bhavnagar",
    "Botad",
    "Chhota Udaipur",
    "Dahod",
    "Dang",
    "Devbhoomi Dwarka",
    "Gandhinagar",
    "Gir Somnath",
    "Jamnagar",
    "Junagadh",
    "Kheda",
    "Kutch",
    "Mahisagar",
    "Mahesana",
    "Morbi",
    "Narmada",
    "Navsari",
    "Panchmahal",
    "Patan",
    "Porbandar",
    "Rajkot",
    "Sabarkantha",
    "Surat",
    "Surendranagar",
    "Tapi",
    "Vadodara",
    "Valsad",
  ],
  Rajasthan: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"],
  Maharashtra: ["Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Solapur"],
  "Uttar Pradesh": ["Lucknow", "Kanpur Nagar", "Varanasi", "Prayagraj", "Agra", "Gorakhpur"],
  "Madhya Pradesh": ["Bhopal", "Indore", "Jabalpur", "Gwalior", "Ujjain"],
};

const FALLBACK_TALUKAS_BY_DISTRICT = {
  Banaskantha: [
    "Amirgadh",
    "Dhanera",
    "Deesa",
    "Danta",
    "Dantiwada",
    "Palanpur",
    "Vadgam",
    "Kankrej",
  ],
  Mahesana: [
    "Mehsana",
    "Vijapur",
    "Kadi",
    "Visnagar",
    "Unjha",
    "Kheralu",
    "Becharaji",
    "Satlasna",
  ],
  Gandhinagar: ["Gandhinagar", "Kalol", "Mansa", "Dehgam"],
  Ahmedabad: ["Ahmedabad City", "Daskroi", "Sanand", "Bavla", "Dholka", "Dhandhuka"],
  Rajkot: ["Rajkot", "Gondal", "Jasdan", "Jetpur", "Dhoraji"],
};

const CAPACITY_OPTIONS = [500, 750, 1000, 1500, 2000];

const APPLICATION_SOURCES = [
  {
    value: "PACS_DIRECT",
    label: "PACS / Society Direct Application",
    public: true,
  },
  {
    value: "PMC_ASSISTED",
    label: "PMC Assisted Application",
    public: true,
  },
  {
    value: "SHINE_AGRI_SELF",
    label: "Shine Agri Self Project",
    public: false,
  },
];

const DEFAULT_PMC = {
  pmcId: "",
  pmcUid: "",
  pmcName: "Not allocated yet",
  pmcEmail: "",
  pmcMobile: "",
  pmcAddress: "",
};

const REMOVED_STATUSES = new Set([
  "DELETED",
  "REMOVED",
  "ARCHIVED",
  "TRASHED",
  "CANCELLED",
  "CANCELED",
]);

/* ================= HELPERS ================= */

const cx = (...xs) => xs.filter(Boolean).join(" ");

function normalizeText(v = "") {
  return String(v || "").trim();
}

function normalizeKey(v = "") {
  return String(v || "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
}

function upper(v = "") {
  return normalizeText(v).toUpperCase();
}

function isRemovedRecord(data = {}) {
  return (
    data.isDeleted === true ||
    data.deleted === true ||
    data.isArchived === true ||
    data.archived === true ||
    Boolean(data.deletedAt || data.archivedAt || data.removedAt) ||
    [
      data.status,
      data.applicationStatus,
      data.registrationStatus,
      data.allocationStatus,
    ].some((value) => REMOVED_STATUSES.has(upper(value)))
  );
}

function isApprovedPacsProfile(profile = {}) {
  const role = upper(profile.role);
  const status = upper(
    profile.status ||
      profile.registrationStatus ||
      profile.roleApprovalStatus
  );

  return (
    ["PACS", "PACS_SOCIETY"].includes(role) &&
    status === "APPROVED" &&
    profile.isActive !== false &&
    !isRemovedRecord(profile)
  );
}

function isActivePmcProfile(profile = {}) {
  const role = upper(profile.role);
  const status = upper(
    profile.status ||
      profile.registrationStatus ||
      profile.roleApprovalStatus
  );

  return (
    ["PMC", "PMC_AGENCY"].includes(role) &&
    ["APPROVED", "ACTIVE"].includes(status) &&
    profile.isActive !== false &&
    !isRemovedRecord(profile)
  );
}

function getPmcIdFromProfile(profile = {}) {
  return normalizeText(
    profile.allocatedPmcUid ||
      profile.allocatedPmcId ||
      profile.assignedPmcUid ||
      profile.assignedPmcId ||
      profile.pmcUid ||
      profile.pmcId
  );
}

function toPmcCard(profile = {}, fallback = {}) {
  const pmcId = normalizeText(
    profile.uid ||
      profile.pmcUid ||
      profile.pmcId ||
      fallback.pmcUid ||
      fallback.pmcId
  );

  return {
    pmcId,
    pmcUid: pmcId,
    pmcName:
      normalizeText(
        profile.organizationName ||
          profile.agencyName ||
          profile.pmcName ||
          profile.name ||
          fallback.pmcName
      ) || "Allocated PMC",
    pmcEmail: normalizeText(
      profile.officialEmail ||
        profile.email ||
        profile.pmcEmail ||
        fallback.pmcEmail
    ),
    pmcMobile: normalizeText(
      profile.officialMobile ||
        profile.mobile ||
        profile.phone ||
        profile.pmcMobile ||
        fallback.pmcMobile
    ),
    pmcAddress: normalizeText(
      profile.officeAddress ||
        profile.address ||
        profile.registeredAddress ||
        fallback.pmcAddress
    ),
  };
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function newestRecord(records = []) {
  return [...records].sort(
    (a, b) =>
      timestampToMillis(b.updatedAt || b.createdAt || b.appliedAt) -
      timestampToMillis(a.updatedAt || a.createdAt || a.appliedAt)
  )[0];
}

function toAmount(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatInr(v) {
  return `₹${toAmount(v).toLocaleString("en-IN")}`;
}

function formatCapacity(mt) {
  const n = Number(mt || 0);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr MT`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)} LMT`;
  return `${n.toLocaleString("en-IN")} MT`;
}

function getFyString() {
  const today = new Date();
  const y = today.getFullYear();
  const month = today.getMonth();
  const fyStartYear = month >= 3 ? y : y - 1;
  const yy1 = String(fyStartYear).slice(2);
  const yy2 = String(fyStartYear + 1).slice(2);
  return yy1 + yy2;
}

function getFyLabel(startYear) {
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

function getLastCompletedFinancialYears(count = 7) {
  const today = new Date();
  const y = today.getFullYear();
  const month = today.getMonth();
  const currentFyStartYear = month >= 3 ? y : y - 1;
  const lastCompletedFyStartYear = currentFyStartYear - 1;

  return Array.from({ length: count }, (_, index) => {
    const startYear = lastCompletedFyStartYear - index;
    const value = getFyLabel(startYear);
    return {
      value,
      label: `FY ${value}`,
    };
  });
}

async function getNextGodownApplicationId() {
  const seriesRef = doc(db, "series", "godown_applications");
  const fy = getFyString();

  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(seriesRef);
    let next = 1;

    if (snap.exists()) {
      const data = snap.data();
      const currentFy = data.fy || fy;

      if (currentFy === fy) next = Number(data.seq || 0) + 1;
      else next = 1;

      tx.update(seriesRef, {
        seq: next,
        fy,
        updatedAt: serverTimestamp(),
      });
    } else {
      tx.set(seriesRef, {
        seq: 1,
        fy,
        updatedAt: serverTimestamp(),
      });
      next = 1;
    }

    return next;
  });

  const num = String(result).padStart(4, "0");
  const appId = `NAFED/${fy}/Godown/${num}`;

  return { appId, seq: result, fy };
}

/* ================= UI ATOMS ================= */

function Label({ children }) {
  return (
    <label className="text-xs font-black text-slate-700 tracking-wide">
      {children}
    </label>
  );
}

function Input({ type = "text", value, onChange, ...p }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm shadow-sm outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
      {...p}
    />
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm shadow-sm outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    >
      {children}
    </select>
  );
}

function PremiumCard({ title, subtitle, children }) {
  return (
    <div className="rounded-[28px] border border-white/80 bg-white/90 p-5 md:p-6 shadow-[0_24px_60px_rgba(15,118,110,0.10)] backdrop-blur-xl">
      {title && (
        <div className="mb-5">
          <h2 className="text-base md:text-lg font-black text-slate-900 tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-xs md:text-sm text-slate-500 leading-6">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

/* ================= DYNAMIC LOCATION / PMC ================= */

async function loadLocationMaster(stateName) {
  try {
    const districtSnap = await getDocs(
      query(
        collection(db, "location_master"),
        where("state", "==", stateName),
        where("type", "==", "district"),
        orderBy("name", "asc")
      )
    );

    const districts = districtSnap.docs.map((d) => d.data()?.name).filter(Boolean);

    if (districts.length > 0) {
      return {
        districts,
        talukasByDistrict: {},
      };
    }

    return {
      districts: FALLBACK_DISTRICTS_BY_STATE[stateName] || [],
      talukasByDistrict: FALLBACK_TALUKAS_BY_DISTRICT,
    };
  } catch (e) {
    console.error("Location master load failed:", e);
    return {
      districts: FALLBACK_DISTRICTS_BY_STATE[stateName] || [],
      talukasByDistrict: FALLBACK_TALUKAS_BY_DISTRICT,
    };
  }
}

async function loadTalukasFromMaster(stateName, districtName) {
  try {
    const snap = await getDocs(
      query(
        collection(db, "location_master"),
        where("state", "==", stateName),
        where("district", "==", districtName),
        where("type", "==", "taluka"),
        orderBy("name", "asc")
      )
    );

    const talukas = snap.docs.map((d) => d.data()?.name).filter(Boolean);

    if (talukas.length > 0) return talukas;

    return FALLBACK_TALUKAS_BY_DISTRICT[districtName] || [];
  } catch (e) {
    console.error("Taluka master load failed:", e);
    return FALLBACK_TALUKAS_BY_DISTRICT[districtName] || [];
  }
}

async function readPmcProfile(pmcId, fallback = {}) {
  const resolvedId = normalizeText(pmcId);
  if (!resolvedId) return DEFAULT_PMC;

  for (const collectionName of ["nafed_users", "pmc_users"]) {
    try {
      const snap = await getDoc(doc(db, collectionName, resolvedId));
      if (!snap.exists()) continue;

      const profile = { id: snap.id, ...snap.data() };
      if (!isActivePmcProfile(profile)) continue;
      return toPmcCard(profile, { ...fallback, pmcId: resolvedId });
    } catch (error) {
      console.warn(`Unable to read ${collectionName}/${resolvedId}:`, error);
    }
  }

  return DEFAULT_PMC;
}

async function findPacsPmcAllocation(pacsUid, pacsProfile = {}) {
  const directPmcId = getPmcIdFromProfile(pacsProfile);
  if (directPmcId) {
    const directPmc = await readPmcProfile(directPmcId, {
      pmcId: directPmcId,
      pmcName:
        pacsProfile.allocatedPmcName ||
        pacsProfile.assignedPmcName ||
        pacsProfile.pmcName,
      pmcEmail:
        pacsProfile.allocatedPmcEmail ||
        pacsProfile.assignedPmcEmail ||
        pacsProfile.pmcEmail,
      pmcMobile:
        pacsProfile.allocatedPmcMobile ||
        pacsProfile.assignedPmcMobile ||
        pacsProfile.pmcMobile,
      pmcAddress:
        pacsProfile.allocatedPmcAddress ||
        pacsProfile.assignedPmcAddress ||
        pacsProfile.pmcAddress,
    });

    if (directPmc.pmcId) return directPmc;
  }

  const candidates = [];
  for (const field of ["pacsUid", "pacsId", "pacsUserId"]) {
    try {
      const snap = await getDocs(
        query(
          collection(db, "pacs_pmc_allocations"),
          where(field, "==", pacsUid)
        )
      );

      snap.docs.forEach((item) => {
        const data = { id: item.id, ...item.data() };
        const allocationStatus = upper(
          data.status || data.allocationStatus || "ACTIVE"
        );

        if (
          !isRemovedRecord(data) &&
          ["ACTIVE", "ALLOCATED", "APPROVED"].includes(allocationStatus)
        ) {
          candidates.push(data);
        }
      });
    } catch (error) {
      console.warn(`PMC allocation lookup by ${field} failed:`, error);
    }
  }

  const allocation = newestRecord(candidates);
  if (!allocation) return DEFAULT_PMC;

  const allocatedPmcId = normalizeText(
    allocation.pmcUid || allocation.pmcId || allocation.allocatedPmcId
  );

  return readPmcProfile(allocatedPmcId, {
    pmcId: allocatedPmcId,
    pmcName: allocation.pmcName || allocation.agencyName,
    pmcEmail: allocation.pmcEmail || allocation.email,
    pmcMobile: allocation.pmcMobile || allocation.mobile,
    pmcAddress: allocation.pmcAddress || allocation.address,
  });
}

async function findExistingActiveApplication(pacsUid) {
  const records = [];

  for (const field of ["pacsUid", "pacsUserId", "createdByUid"]) {
    try {
      const snap = await getDocs(
        query(collection(db, "godown_projects"), where(field, "==", pacsUid))
      );

      snap.docs.forEach((item) => {
        const data = { id: item.id, ...item.data() };
        const isNafed =
          data.nafedProject === true ||
          upper(data.projectSource) === "NAFED" ||
          upper(data.applicationPortal) === "NAFED_PACS_APPLY";

        if (isNafed && !isRemovedRecord(data)) records.push(data);
      });
    } catch (error) {
      console.warn(`Existing project lookup by ${field} failed:`, error);
    }
  }

  const unique = Array.from(
    new Map(records.map((item) => [item.id || item.applicationId, item])).values()
  );
  return newestRecord(unique) || null;
}

/* ================= PDF ================= */

function downloadApplicationPDF(data) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;
  const bottomY = pageHeight - 44;

  let y = 48;

  const safe = (v) => String(v == null ? "" : v);
  const moneyPdf = (v) => `Rs. ${toAmount(v).toLocaleString("en-IN")}`;

  const ensureSpace = (needed = 60) => {
    if (y + needed > bottomY) {
      addFooter();
      pdf.addPage();
      y = 48;
    }
  };

  const addFooter = () => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(115, 115, 115);
    pdf.text(
      "System-generated NAFED Grain Storage Project application. Final approval is subject to verification by competent authorities.",
      marginX,
      pageHeight - 22
    );
    pdf.setTextColor(0, 0, 0);
  };

  const writeWrapped = ({
    text,
    x = marginX,
    width = contentWidth,
    lineHeight = 13,
    font = "normal",
    size = 10.5,
  }) => {
    pdf.setFont("helvetica", font);
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(safe(text), width);
    pdf.text(lines, x, y);
    y += lines.length * lineHeight;
  };

  pdf.setFillColor(235, 253, 245);
  pdf.setDrawColor(16, 185, 129);
  pdf.roundedRect(marginX, 28, contentWidth, 62, 10, 10, "FD");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(5, 95, 70);
  pdf.text("NAFED GRAIN STORAGE PROJECT APPLICATION", pageWidth / 2, 52, {
    align: "center",
  });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(60, 60, 60);
  pdf.text(
    "World's Largest Grain Storage Plan in Cooperative Sector",
    pageWidth / 2,
    70,
    { align: "center" }
  );

  pdf.setTextColor(0, 0, 0);
  y = 112;

  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(190, 190, 190);
  pdf.roundedRect(marginX, y, contentWidth, 88, 8, 8, "FD");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  pdf.text("Application Details", marginX + 14, y + 20);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.text(`Application ID: ${safe(data.applicationId)}`, marginX + 14, y + 42);
  pdf.text(`Date: ${dayjs(data.createdAt || new Date()).format("DD/MM/YYYY")}`, marginX + 355, y + 42);
  pdf.text(`State: ${safe(data.state)}`, marginX + 14, y + 60);
  pdf.text(`District: ${safe(data.district)}`, marginX + 220, y + 60);
  pdf.text(`Allocated PMC: ${safe(data.allocatedPmcName || "Not allocated yet")}`, marginX + 14, y + 78);

  y += 116;

  pdf.text("To,", marginX, y);
  y += 14;
  pdf.text("The Competent Authority,", marginX, y);
  y += 14;
  pdf.text("NAFED / State Branch / Cooperative Department,", marginX, y);
  y += 14;
  pdf.text(`${safe(data.district)}, ${safe(data.state)}.`, marginX, y);
  y += 24;

  writeWrapped({
    text:
      "Subject: Submission of application for Grain Storage Godown Project under the World's Largest Grain Storage Plan in Cooperative Sector.",
    font: "bold",
  });

  y += 10;

  writeWrapped({
    text:
      `Respected Sir/Madam,\n\n` +
      `We hereby submit this application for construction / development of a ${safe(data.capacityMt)} MT grain storage godown under the World's Largest Grain Storage Plan in Cooperative Sector.\n\n` +
      `Applicant Institution: ${safe(data.pacsName)}\n` +
      `Registration No.: ${safe(data.pacsRegistrationNo)}\n` +
      `Project Location: ${safe(data.village)}, ${safe(data.taluka)}, ${safe(data.district)}, ${safe(data.state)}\n\n` +
      `The application is being submitted for working under the NAFED project ecosystem. The project shall be processed through district/state level verification, PMC coordination, DPR, bank appraisal, tender process and construction monitoring as per applicable guidelines.\n\n` +
      `Allocated PMC / Handholding Agency: ${safe(data.allocatedPmcName || "Not allocated yet")}.\n\n` +
      `We request your office to accept this application for further scrutiny and processing.\n\n` +
      `Yours faithfully,`,
  });

  y += 22;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`For: ${safe(data.pacsName)}`, marginX, y);
  y += 16;
  pdf.text("Authorized Signatory: ______________________________", marginX, y);
  y += 16;
  pdf.text(`Contact Mobile: ${safe(data.contactPhone)}`, marginX, y);
  y += 16;
  pdf.text("Seal: ______________________________", marginX, y);

  addFooter();

  pdf.addPage();
  y = 48;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(5, 95, 70);
  pdf.text("Annexure-A: Financial Strength Statement", pageWidth / 2, y, {
    align: "center",
  });

  pdf.setTextColor(0, 0, 0);
  y += 34;

  const financial = data.financialDetails || {};
  const profits = Array.isArray(financial.profits) ? financial.profits : [];

  const totalProfit =
    financial?.totals?.totalProfit != null
      ? toAmount(financial.totals.totalProfit)
      : profits.reduce((sum, row) => sum + toAmount(row.profit), 0);

  const averageProfit =
    financial?.totals?.averageProfit != null
      ? toAmount(financial.totals.averageProfit)
      : Math.round(totalProfit / Math.max(profits.length, 1));

  const totalFunds =
    financial?.totals?.totalFunds != null
      ? toAmount(financial.totals.totalFunds)
      : toAmount(financial.reserveFund) +
        toAmount(financial.buildingFund) +
        toAmount(financial.shareCapital);

  writeWrapped({
    text:
      "This financial summary is submitted by the applicant institution for preliminary review. Values are subject to verification from audit reports and official records.",
    size: 10,
  });

  y += 20;

  const drawRow = (label, value, bold = false) => {
    ensureSpace(28);
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(10);
    pdf.text(label, marginX, y);
    pdf.text(value, pageWidth - marginX, y, { align: "right" });
    y += 24;
  };

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text("Profit Details", marginX, y);
  y += 24;

  profits.forEach((row, index) => {
    drawRow(`${index + 1}. FY ${row.fy}`, moneyPdf(row.profit));
  });

  drawRow("Total Profit", moneyPdf(totalProfit), true);
  drawRow("Average Annual Profit", moneyPdf(averageProfit), true);

  y += 18;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text("Fund / Capital Summary", marginX, y);
  y += 24;

  drawRow("Reserve Fund", moneyPdf(financial.reserveFund));
  drawRow("Building / Makan Fund", moneyPdf(financial.buildingFund));
  drawRow("Share Capital", moneyPdf(financial.shareCapital));
  drawRow("Total Fund Strength", moneyPdf(totalFunds), true);
  drawRow("Audit Class", safe(financial.auditClass || "-"));

  addFooter();

  const fileName = safe(data.applicationId || "nafed_godown_application").replace(/\//g, "_");
  pdf.save(fileName + ".pdf");
}

/* ================= NOTIFICATIONS / LINKING ================= */

async function queuePmcNotificationOnNewApplication(payload) {
  if (!payload.allocatedPmcId) return;

  try {
    await addDoc(collection(db, "notifications_pmc"), {
      type: "NAFED_GODOWN_APPLICATION_SUBMITTED",
      applicationId: payload.applicationId,
      pacsName: payload.pacsName,
      pacsRegistrationNo: payload.pacsRegistrationNo,
      state: payload.state,
      district: payload.district,
      taluka: payload.taluka,
      village: payload.village,
      capacityMt: payload.capacityMt,
      contactName: payload.contactName,
      contactPhone: payload.contactPhone,
      allocatedPmcId: payload.allocatedPmcId || "",
      allocatedPmcUid: payload.allocatedPmcId || "",
      recipientUid: payload.allocatedPmcId || "",
      recipientRole: "PMC",
      allocatedPmcName: payload.allocatedPmcName || "",
      source: payload.applicationSource || "PACS_DIRECT",
      status: "PENDING",
      isRead: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Notification queue failed:", e);
  }
}

async function linkApplicationToOutreachOrUnmatched({
  invitationCode,
  applicationId,
  pacsName,
  contactName,
  contactPhone,
  contactEmail,
  state,
  district,
  taluka,
  village,
  capacityMt,
}) {
  const now = serverTimestamp();
  const code = String(invitationCode || "").trim();

  if (!code) {
    await addDoc(collection(db, "unmatched_applications"), {
      applicationId,
      pacsName: pacsName || "",
      contactName: contactName || "",
      state: state || "",
      district: district || "",
      taluka: taluka || "",
      village: village || "",
      contactPhone: (contactPhone || "").trim(),
      contactEmail: (contactEmail || "").trim(),
      capacityMt: Number(capacityMt || 0) || null,
      source: "DIRECT_NAFED_PORTAL",
      createdAt: now,
    });

    return { linked: false, mode: "DIRECT_NAFED_PORTAL" };
  }

  try {
    const q1 = query(
      collection(db, "outreach_recipients"),
      where("uniqueCode", "==", code)
    );

    const snap = await getDocs(q1);

    if (!snap.empty) {
      const d = snap.docs[0];

      await updateDoc(doc(db, "outreach_recipients", d.id), {
        outreachStatus: "APPLIED",
        appliedApplicationId: applicationId,
        appliedAt: now,
        updatedAt: now,
        societyName: pacsName || d.data()?.societyName || "",
        state: state || d.data()?.state || "",
        district: district || d.data()?.district || "",
        taluka: taluka || d.data()?.taluka || "",
        village: village || d.data()?.village || "",
        mobile: (contactPhone || "").trim() || d.data()?.mobile || "",
        email: (contactEmail || "").trim() || d.data()?.email || "",
      });

      return { linked: true, mode: "OUTREACH", outreachId: d.id };
    }

    await addDoc(collection(db, "unmatched_applications"), {
      applicationId,
      pacsName: pacsName || "",
      contactName: contactName || "",
      state: state || "",
      district: district || "",
      taluka: taluka || "",
      village: village || "",
      contactPhone: (contactPhone || "").trim(),
      contactEmail: (contactEmail || "").trim(),
      capacityMt: Number(capacityMt || 0) || null,
      source: "INVITE_CODE_NOT_FOUND",
      invitationCode: code,
      createdAt: now,
    });

    return { linked: false, mode: "UNMATCHED_INVITE_CODE_NOT_FOUND" };
  } catch (e) {
    console.error("Application linking failed:", e);
    return { linked: false, mode: "LINKING_FAILED" };
  }
}

/* ================= MODAL ================= */

function FullScreenModal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="absolute inset-0 p-3 md:p-6 flex">
        <div className="w-full max-w-7xl mx-auto rounded-[32px] bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-white">
            <div>
              <div className="text-[11px] font-black tracking-[0.18em] text-emerald-700 uppercase">
                NAFED Digital Portal
              </div>
              <div className="text-sm md:text-base font-black text-slate-900">
                {title}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-bold hover:bg-slate-800"
            >
              Close ✕
            </button>
          </div>

          <div className="flex-1 overflow-auto p-4 md:p-6 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
/* ================== MAIN COMPONENT ================== */

export default function NafedGodownApplyForm() {
  const [showForm, setShowForm] = useState(false);

  const [applicationSource, setApplicationSource] = useState("PACS_DIRECT");

  const [pacsName, setPacsName] = useState("");
  const [pacsRegNo, setPacsRegNo] = useState("");
  const [pacsType, setPacsType] = useState("PACS");

  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [state, setState] = useState("Gujarat");
  const [district, setDistrict] = useState("");
  const [taluka, setTaluka] = useState("");
  const [village, setVillage] = useState("");

  const [districtOptions, setDistrictOptions] = useState([]);
  const [talukaOptions, setTalukaOptions] = useState([]);

  const [allocatedPmc, setAllocatedPmc] = useState(DEFAULT_PMC);
  const [pmcLoading, setPmcLoading] = useState(false);

  const [capacityMt, setCapacityMt] = useState("1000");
  const [godownType, setGodownType] = useState("1000 MT Godown");

  const [plotLength, setPlotLength] = useState("");
  const [plotWidth, setPlotWidth] = useState("");
  const [landDetails, setLandDetails] = useState("");
  const [mapUrl, setMapUrl] = useState("");

  const [regFiles, setRegFiles] = useState([]);
  const [landFiles, setLandFiles] = useState([]);
  const [committeeFiles, setCommitteeFiles] = useState([]);
  const [auditFiles, setAuditFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);

  const [reserveFund, setReserveFund] = useState("");
  const [buildingFund, setBuildingFund] = useState("");
  const [shareCapital, setShareCapital] = useState("");
  const [auditClass, setAuditClass] = useState("");

  const [profitRows, setProfitRows] = useState(() =>
    getLastCompletedFinancialYears(3).map((fy) => ({
      fy: fy.value,
      profit: "",
    }))
  );

  const [submitting, setSubmitting] = useState(false);

  const [checkId, setCheckId] = useState("");
  const [checkMobile, setCheckMobile] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const [lastAppId, setLastAppId] = useState("");
  const [lastAppDate, setLastAppDate] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [pacsProfile, setPacsProfile] = useState(null);
  const [currentPacsUid, setCurrentPacsUid] = useState("");
  const [activeApplication, setActiveApplication] = useState(null);
  const [liveSyncAt, setLiveSyncAt] = useState(null);

  useEffect(() => {
    let unsubscribeProfile = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = "/nafed";
        return;
      }

      setCurrentPacsUid(user.uid);
      setProfileLoading(true);

      unsubscribeProfile();
      unsubscribeProfile = onSnapshot(
        doc(db, "nafed_users", user.uid),
        (profileSnap) => {
        if (!profileSnap.exists()) {
          toast.error("PACS registration profile was not found.");
          window.location.href = "/nafed";
          return;
        }

          const profile = { uid: user.uid, ...profileSnap.data() };
          if (!isApprovedPacsProfile(profile)) {
          toast.error("Only an approved PACS account can submit this application.");
          window.location.href = "/nafed";
          return;
        }

          setPacsProfile(profile);
          setPacsName(
            profile.organizationName ||
              profile.societyName ||
              profile.pacsName ||
              ""
          );
          setPacsRegNo(
            profile.registrationNumber ||
              profile.pacsRegistrationNo ||
              profile.registrationNo ||
              ""
          );
          setContactName(
            profile.authorizedPersonName ||
              profile.contactName ||
              profile.name ||
              ""
          );
          setContactPhone(profile.mobile || profile.phone || "");
          setContactEmail(profile.email || user.email || "");
          setState(profile.state || profile.stateName || "Gujarat");
          setDistrict(profile.district || profile.districtName || "");
          setTaluka(profile.taluka || profile.block || "");
          setVillage(profile.village || profile.location || "");
          setProfileLoading(false);
          setLiveSyncAt(new Date());
        },
        (error) => {
          console.error("PACS profile live loading failed:", error);
          toast.error("Unable to load PACS registration profile.");
          setProfileLoading(false);
        }
      );
    });

    return () => {
      unsubscribeProfile();
      unsubscribeAuth();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolvePmc() {
      if (!currentPacsUid || !pacsProfile) {
        setAllocatedPmc(DEFAULT_PMC);
        return;
      }

      try {
        setPmcLoading(true);
        const pmc = await findPacsPmcAllocation(currentPacsUid, pacsProfile);
        if (!cancelled) {
          setAllocatedPmc(pmc?.pmcId ? pmc : DEFAULT_PMC);
          setLiveSyncAt(new Date());
        }
      } finally {
        if (!cancelled) setPmcLoading(false);
      }
    }

    resolvePmc();
    return () => {
      cancelled = true;
    };
  }, [currentPacsUid, pacsProfile]);

  useEffect(() => {
    if (!currentPacsUid) return undefined;

    const liveQuery = query(
      collection(db, "godown_projects"),
      where("pacsUid", "==", currentPacsUid)
    );

    return onSnapshot(
      liveQuery,
      (snapshot) => {
        const records = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => {
            const isNafed =
              item.nafedProject === true ||
              upper(item.projectSource) === "NAFED" ||
              upper(item.applicationPortal) === "NAFED_PACS_APPLY";
            return isNafed && !isRemovedRecord(item);
          });

        const latest = newestRecord(records) || null;
        setActiveApplication(latest);
        if (latest) {
          setCheckResult(latest);
          setLastAppId(latest.applicationId || "");
          setLastAppDate(latest.createdAt?.toDate?.() || null);
        }
        setLiveSyncAt(new Date());
      },
      (error) => {
        console.warn("Live PACS application listener failed:", error);
      }
    );
  }, [currentPacsUid]);

  const numericLength = Number(plotLength) || 0;
  const numericWidth = Number(plotWidth) || 0;
  const landArea = numericLength * numericWidth;

  const financialYearOptions = getLastCompletedFinancialYears(7);

  const totalProfit = profitRows.reduce(
    (sum, row) => sum + toAmount(row.profit),
    0
  );

  const averageProfit =
    profitRows.length > 0 ? Math.round(totalProfit / profitRows.length) : 0;

  const totalFunds =
    toAmount(reserveFund) + toAmount(buildingFund) + toAmount(shareCapital);

  const selectedApplicationSource = APPLICATION_SOURCES.find(
    (x) => x.value === applicationSource
  );

  const visibleApplicationSources = APPLICATION_SOURCES.filter(
    (x) => x.public || auth.currentUser?.email === "ceo@shineagri.org"
  );

  useEffect(() => {
    async function run() {
      setTalukaOptions([]);

      const master = await loadLocationMaster(state);
      setDistrictOptions(master.districts || []);
    }

    run();
  }, [state]);

  useEffect(() => {
    async function run() {
      setTalukaOptions([]);

      if (!state || !district) return;

      const talukas = await loadTalukasFromMaster(state, district);
      setTalukaOptions(talukas || []);
    }

    run();
  }, [state, district]);

  const updateProfitRow = (index, field, value) => {
    setProfitRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const hasDuplicateFy = (rows) => {
    const years = rows.map((r) => r.fy).filter(Boolean);
    return new Set(years).size !== years.length;
  };

  const buildFinancialDetailsPayload = () => ({
    reserveFund: toAmount(reserveFund),
    buildingFund: toAmount(buildingFund),
    shareCapital: toAmount(shareCapital),
    auditClass: String(auditClass || "").trim(),

    profits: profitRows.map((row, index) => ({
      yearNo: index + 1,
      fy: row.fy,
      profit: toAmount(row.profit),
    })),

    totals: {
      totalProfit,
      averageProfit,
      totalFunds,
    },
  });

  const resetFinancialDetails = () => {
    setReserveFund("");
    setBuildingFund("");
    setShareCapital("");
    setAuditClass("");

    const defaults = getLastCompletedFinancialYears(3);

    setProfitRows(
      defaults.map((fy) => ({
        fy: fy.value,
        profit: "",
      }))
    );
  };

  const validate = () => {
    if (!auth.currentUser) {
      toast.error("Please login with an approved PACS account.");
      return false;
    }
    if (!isApprovedPacsProfile(pacsProfile || {})) {
      toast.error("Your PACS profile is not approved or is inactive.");
      return false;
    }
    if (!allocatedPmc?.pmcId) {
      toast.error(
        "State Branch has not allocated a PMC to this PACS. Please contact State Branch."
      );
      return false;
    }
    if (!applicationSource) {
      toast.error("Please select application source.");
      return false;
    }

    if (applicationSource === "SHINE_AGRI_SELF" && auth.currentUser?.email !== "ceo@shineagri.org") {
      toast.error("Shine Agri self project option is restricted.");
      return false;
    }

    if (!pacsName || !pacsRegNo || !contactName || !contactPhone) {
      toast.error("Institution name, registration number and contact details are mandatory.");
      return false;
    }

    if (contactPhone.length !== 10) {
      toast.error("Mobile number must be 10 digits.");
      return false;
    }

    if (!state || !district || !taluka || !village) {
      toast.error("Please fill State, District, Taluka and Village.");
      return false;
    }

    if (
      !capacityMt ||
      isNaN(Number(capacityMt)) ||
      !CAPACITY_OPTIONS.includes(Number(capacityMt))
    ) {
      toast.error("Please select proposed capacity.");
      return false;
    }

    if (mapUrl && !/^https?:\/\//i.test(mapUrl.trim())) {
      toast.error("Please enter a valid Google Map link starting with http/https.");
      return false;
    }

    if (hasDuplicateFy(profitRows)) {
      toast.error("Duplicate financial year is not allowed in profit section.");
      return false;
    }

    if (profitRows.some((row) => !row.fy)) {
      toast.error("Please select financial year for last 3 years profit.");
      return false;
    }

    if (profitRows.some((row) => String(row.profit).trim() === "")) {
      toast.error("Please enter last 3 years profit amount. Enter negative amount in case of loss.");
      return false;
    }

    if (!regFiles.length) {
      toast.error("Registration certificate upload is required.");
      return false;
    }

    if (!landFiles.length) {
      toast.error("Land documents upload is required.");
      return false;
    }

    if (!committeeFiles.length) {
      toast.error("Board / managing committee resolution upload is required.");
      return false;
    }

    if (!auditFiles.length) {
      toast.error("Last 3 years audit reports upload is required.");
      return false;
    }

    const allFiles = [
      ...regFiles,
      ...landFiles,
      ...committeeFiles,
      ...auditFiles,
      ...otherFiles,
    ];
    const allowedTypes = new Set([
      "application/pdf",
      "image/jpeg",
      "image/png",
    ]);
    const invalidFile = allFiles.find(
      (file) => !allowedTypes.has(file.type) || file.size > 10 * 1024 * 1024
    );
    if (invalidFile) {
      toast.error(
        `${invalidFile.name}: only PDF/JPG/PNG up to 10 MB is allowed.`
      );
      return false;
    }

    return true;
  };

  const uploadGroup = async (files, appId, folder) => {
    const out = [];

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `godown_applications/${appId.replace(
        /\//g,
        "_"
      )}/${folder}/${Date.now()}_${index}_${safeName}`;
      const ref = storageRef(storage, path);

      await uploadBytes(ref, file, {
        contentType: file.type || "application/octet-stream",
        customMetadata: {
          applicationId: appId,
          pacsUid: auth.currentUser?.uid || "",
          documentGroup: folder,
        },
      });

      const url = await getDownloadURL(ref);

      out.push({
        name: file.name,
        url,
        path,
        contentType: file.type || "",
        size: Number(file.size || 0),
        uploadedAt: new Date().toISOString(),
      });
    }

    return out;
  };

  const resetFormAfterSubmit = () => {
    setApplicationSource("PACS_DIRECT");

    setPacsName(
      pacsProfile?.organizationName ||
        pacsProfile?.societyName ||
        pacsProfile?.pacsName ||
        ""
    );
    setPacsRegNo(
      pacsProfile?.registrationNumber ||
        pacsProfile?.pacsRegistrationNo ||
        pacsProfile?.registrationNo ||
        ""
    );
    setPacsType("PACS");

    setContactName(
      pacsProfile?.authorizedPersonName ||
        pacsProfile?.contactName ||
        pacsProfile?.name ||
        ""
    );
    setContactPhone(pacsProfile?.mobile || pacsProfile?.phone || "");
    setInvitationCode("");
    setContactEmail(
      pacsProfile?.email || auth.currentUser?.email || ""
    );

    setState(pacsProfile?.state || pacsProfile?.stateName || "Gujarat");
    setDistrict(pacsProfile?.district || pacsProfile?.districtName || "");
    setTaluka(pacsProfile?.taluka || pacsProfile?.block || "");
    setVillage(pacsProfile?.village || pacsProfile?.location || "");

    setCapacityMt("1000");
    setGodownType("1000 MT Godown");

    setPlotLength("");
    setPlotWidth("");
    setLandDetails("");
    setMapUrl("");

    setRegFiles([]);
    setLandFiles([]);
    setCommitteeFiles([]);
    setAuditFiles([]);
    setOtherFiles([]);

    resetFinancialDetails();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setSubmitting(true);

      const existingApplication =
        activeApplication ||
        (await findExistingActiveApplication(auth.currentUser.uid));
      if (existingApplication) {
        setActiveApplication(existingApplication);
        setCheckResult(existingApplication);
        toast.error(
          `An active NAFED application already exists: ${
            existingApplication.applicationId || existingApplication.id
          }`
        );
        return;
      }

      const normalizedState = normalizeText(state);
      const normalizedDistrict = normalizeText(district);
      const normalizedTaluka = normalizeText(taluka);
      const normalizedVillage = normalizeText(village);

      const { appId, fy, seq } = await getNextGodownApplicationId();

      const registrationDocs = await uploadGroup(regFiles, appId, "registration");
      const landDocs = await uploadGroup(landFiles, appId, "land");
      const committeeDocs = await uploadGroup(committeeFiles, appId, "committee");
      const auditDocs = await uploadGroup(auditFiles, appId, "audit");
      const otherDocs = await uploadGroup(otherFiles, appId, "other");

      const createdByUid = auth.currentUser?.uid || null;
      const createdByEmail = auth.currentUser?.email || "";
      const now = serverTimestamp();
      const financialDetails = buildFinancialDetailsPayload();

      const applicationPayload = {
        applicationId: appId,
        applicationSeq: seq,
        financialYear: fy,

        nafedProject: true,
  projectSource: "NAFED",
  applicationPortal: "NAFED_PACS_APPLY",
  portalRoute: "/nafed/apply-godown",

        applicationSource,
        applicationSourceLabel: selectedApplicationSource?.label || applicationSource,

        source:
          applicationSource === "SHINE_AGRI_SELF"
            ? "SHINE_AGRI_SELF"
            : applicationSource === "PMC_ASSISTED"
            ? "PMC_ASSISTED"
            : "PACS_DIRECT",

        createdByUid,
        pacsUid: createdByUid,
        pacsId: createdByUid,
        pacsUserId: createdByUid,
        createdByEmail,
        createdByRole: "PACS",

        state: normalizedState,
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,

        allocatedPmcUid:
          allocatedPmc?.pmcUid || allocatedPmc?.pmcId || "",
        allocatedPmcId: allocatedPmc?.pmcId || allocatedPmc?.pmcUid || "",
        allocatedPmcName: allocatedPmc?.pmcName || "Not allocated yet",
        allocatedPmcEmail: allocatedPmc?.pmcEmail || "",
        allocatedPmcMobile: allocatedPmc?.pmcMobile || "",
        allocatedPmcAddress: allocatedPmc?.pmcAddress || "",
        pmcUid: allocatedPmc?.pmcUid || allocatedPmc?.pmcId || "",
        pmcId: allocatedPmc?.pmcId || allocatedPmc?.pmcUid || "",
        pmcName: allocatedPmc?.pmcName || "",

        pacsName: pacsName.trim(),
        pacsRegistrationNo: pacsRegNo.trim(),
        pacsType,

        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: String(contactEmail || "").trim(),

        invitationCode: String(invitationCode || "").trim(),

        capacityMt: Number(capacityMt),
        capacityMT: Number(capacityMt),
        storageCapacity: Number(capacityMt),
        storageCapacityMT: Number(capacityMt),
        projectCapacityMT: Number(capacityMt),
        godownType,

        plotLength: numericLength,
        plotWidth: numericWidth,
        landArea,
        landDetails: landDetails.trim(),
        googleMapUrl: mapUrl.trim(),

        financialDetails,

        documents: {
          registrationDocs,
          landDocs,
          committeeDocs,
          auditDocs,
          otherDocs,
        },

        status: "APPLIED",
        applicationStatus: "APPLIED",
        projectStatus: "APPLIED",
        stage: "APPLICATION",
        currentStage: "APPLICATION",

        districtRegistrarStatus: "PENDING",
        stateRegistrarStatus: "PENDING",
        bankStatus: "PENDING",
        caVerificationStatus: "PENDING",

        siteVisitStatus: "NEW",
        siteVisitAssignedTo: null,
        siteVisitPlanDate: null,
        siteVisitReportText: "",
        siteVisitDocs: [],

        pmcDecision: "PENDING",
        pmcDecisionRemark: "",
        pmcDecisionAt: null,

        workflowProjectId: null,
        workflowProgress: 0,
        workflowStage: null,
        workflowUpdatedAt: null,

        tenderStage: "NOT_STARTED",
        linkedTenderId: null,

        createdAt: now,
        updatedAt: now,
      };

      const projectRef = await addDoc(
        collection(db, "godown_projects"),
        applicationPayload
      );

      try {
        await updateDoc(doc(db, "nafed_users", createdByUid), {
          activeProjectId: projectRef.id,
          currentProjectId: projectRef.id,
          applicationId: appId,
          applicationStatus: "APPLIED",
          projectStatus: "APPLIED",
          capacityMt: Number(capacityMt),
          capacityMT: Number(capacityMt),
          projectAppliedAt: now,
          updatedAt: now,
        });
      } catch (profileUpdateError) {
        console.warn(
          "Application saved, but PACS profile link update was skipped:",
          profileUpdateError
        );
      }

      await linkApplicationToOutreachOrUnmatched({
        invitationCode,
        applicationId: appId,
        pacsName: pacsName.trim(),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: String(contactEmail || "").trim(),
        state: normalizedState,
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,
        capacityMt: Number(capacityMt),
      });

      const createdAtDate = new Date();

      downloadApplicationPDF({
        applicationId: appId,
        financialYear: fy,

        applicationSource,
        applicationSourceLabel: selectedApplicationSource?.label || applicationSource,

        pacsName,
        pacsRegistrationNo: pacsRegNo,
        pacsType,
        contactName,
        contactPhone,
        contactEmail,

        village: normalizedVillage,
        taluka: normalizedTaluka,
        district: normalizedDistrict,
        state: normalizedState,

        allocatedPmcId: allocatedPmc?.pmcId || allocatedPmc?.pmcUid || "",
        allocatedPmcName: allocatedPmc?.pmcName || "Not allocated yet",
        allocatedPmcEmail: allocatedPmc?.pmcEmail || "",
        allocatedPmcMobile: allocatedPmc?.pmcMobile || "",

        googleMapUrl: mapUrl,
        capacityMt,
        godownType,
        plotLength: numericLength,
        plotWidth: numericWidth,
        landArea,
        landDetails,

        financialDetails,
        createdAt: createdAtDate,
      });

      await queuePmcNotificationOnNewApplication({
        applicationId: appId,
        pacsName: pacsName.trim(),
        pacsRegistrationNo: pacsRegNo.trim(),
        state: normalizedState,
        district: normalizedDistrict,
        taluka: normalizedTaluka,
        village: normalizedVillage,
        capacityMt: Number(capacityMt),
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        allocatedPmcId: allocatedPmc?.pmcId || allocatedPmc?.pmcUid || "",
        allocatedPmcName: allocatedPmc?.pmcName || "",
        applicationSource,
      });

      toast.success(`Application submitted successfully. Your Application ID is ${appId}.`);

      setLastAppId(appId);
      setLastAppDate(createdAtDate);

      resetFormAfterSubmit();
      setShowForm(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Application submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusCheck = async (e) => {
    e.preventDefault();

    if (!checkId || !checkMobile) {
      toast.error("Please enter both Application ID and registered mobile number.");
      return;
    }

    try {
      setChecking(true);

      const q1 = query(
        collection(db, "godown_projects"),
        where("applicationId", "==", checkId.trim())
      );

      const snap = await getDocs(q1);
      const matched = snap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .find(
          (item) =>
            normalizeText(item.contactPhone) === checkMobile.trim() &&
            !isRemovedRecord(item) &&
            (item.nafedProject === true ||
              upper(item.projectSource) === "NAFED" ||
              upper(item.applicationPortal) === "NAFED_PACS_APPLY")
        );

      if (!matched) {
        setCheckResult(null);
        toast.error("Application not found. Please check Application ID and mobile number.");
        return;
      }

      setCheckResult(matched);
    } catch (e) {
      console.error(e);
      toast.error("Status check failed.");
    } finally {
      setChecking(false);
    }
  };

  const renderStatusLine = (label, value) => (
    <div className="flex justify-between gap-3 text-[11px] md:text-[12px]">
      <span className="font-semibold text-slate-700">{label}</span>
      <span className="text-slate-700 text-right">{value || "—"}</span>
    </div>
  );

  const statusBadge = (result) => {
    if (!result) return null;

    const status = upper(
      result.status || result.applicationStatus || result.projectStatus || "APPLIED"
    );

    let text = status.replaceAll("_", " ");
    let color = "bg-amber-100 text-amber-800 border border-amber-200";

    if (
      [
        "APPROVED",
        "IN_PROGRESS",
        "WORK_STARTED",
        "UNDER_IMPLEMENTATION",
        "COMPLETED",
      ].includes(status)
    ) {
      color = "bg-emerald-100 text-emerald-800 border border-emerald-200";
    } else if (["REJECTED", "CANCELLED", "CANCELED"].includes(status)) {
      color = "bg-red-100 text-red-800 border border-red-200";
    } else if (["QUERY", "QUERY_RAISED", "CORRECTION_REQUIRED"].includes(status)) {
      color = "bg-orange-100 text-orange-800 border border-orange-200";
    }

    return (
      <span className={cx("px-3 py-1 rounded-full text-[11px] font-semibold", color)}>
        Overall Status: {text}
      </span>
    );
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-700 font-bold">
        Loading approved PACS profile...
      </div>
    );
  }

    return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 pb-10 pt-8">
        <div className="max-w-7xl mx-auto px-3 md:px-5">
          <div className="rounded-[34px] bg-white/90 border border-white shadow-[0_30px_80px_rgba(15,118,110,0.16)] px-5 py-6 md:px-10 md:py-9 backdrop-blur-xl">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-800">
                  NAFED Grain Storage Digital Application
                </div>

                <h1 className="mt-5 text-3xl md:text-5xl font-black text-slate-950 tracking-tight leading-tight">
                  World’s Largest Grain Storage Plan in Cooperative Sector
                </h1>

                <p className="mt-4 max-w-3xl text-sm md:text-base text-slate-600 leading-8">
                  Submit a digital application for a proposed grain storage godown
                  project under the NAFED project ecosystem. The application will
                  be routed state-wise and district-wise to the allocated PMC,
                  registrar, bank, CA and approval workflow as applicable.
                </p>

                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
                    <div className="text-sm font-black text-emerald-900">
                      Eligible Applicants
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-7">
                      PACS, cooperative societies, eligible institutions and
                      approved project applicants.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-blue-100 bg-blue-50/70 p-5">
                    <div className="text-sm font-black text-blue-900">
                      District-wise PMC Allocation
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-7">
                      Once the applicant selects State and District, the allocated
                      PMC will be shown automatically based on district allocation.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-amber-100 bg-amber-50/80 p-5">
                    <div className="text-sm font-black text-amber-900">
                      Auto PDF Download
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-7">
                      After successful submission, the application letter and
                      financial annexure PDF will download automatically.
                    </p>
                  </div>
                </div>

                <PremiumCard
                  title="Required Documents"
                  subtitle="Keep scanned copies ready before starting the online application."
                >
                  <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-700 leading-7">
                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <b>1. Registration Certificate</b>
                      <br />
                      Latest registration certificate of applicant institution.
                    </div>

                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <b>2. Land Documents</b>
                      <br />
                      7/12, 8A, sale deed, lease deed, mutation records or
                      applicable land records.
                    </div>

                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <b>3. Board Resolution</b>
                      <br />
                      Board / managing committee resolution for project proposal.
                    </div>

                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <b>4. Audit Reports</b>
                      <br />
                      Last 3 years audit reports and financial statements.
                    </div>
                  </div>
                </PremiumCard>

                <div className="mt-5 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 leading-7">
                  <b>Important Note:</b> Submission of this application does not
                  mean automatic approval. Final approval, subsidy, bank finance,
                  DPR acceptance and construction permission shall be subject to
                  verification and approval by competent authorities under
                  applicable guidelines.
                </div>
              </div>

              <div className="w-full lg:w-[390px]">
                <div className="rounded-[34px] border border-white bg-white/95 p-6 shadow-[0_28px_70px_rgba(6,29,69,0.14)]">
                  <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
                    Apply Online
                  </div>

                  <h2 className="mt-2 text-xl font-black text-slate-950">
                    Start Your Application
                  </h2>

                  <p className="mt-3 text-sm text-slate-600 leading-7">
                    Fill the application, upload required documents and download
                    the system-generated application PDF.
                  </p>

                  <button
                    type="button"
                    disabled={!allocatedPmc.pmcId || Boolean(activeApplication)}
                    onClick={() => setShowForm(true)}
                    className={cx(
                      "mt-5 w-full rounded-2xl px-5 py-3.5 text-sm font-black text-white shadow-lg",
                      !allocatedPmc.pmcId || activeApplication
                        ? "cursor-not-allowed bg-slate-400"
                        : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:shadow-xl"
                    )}
                  >
                    {activeApplication
                      ? "Application Already Submitted"
                      : allocatedPmc.pmcId
                      ? "Apply Now"
                      : "PMC Allocation Pending"}
                  </button>

                  <a
                    href="/nafed"
                    className="mt-3 flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-800 hover:bg-slate-50"
                  >
                    ← Back to NAFED Home
                  </a>

                  <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-700">
                      Your Allocated PMC Helpdesk
                    </div>

                    <div className="mt-3 text-sm text-slate-700 leading-7">
                      {pmcLoading ? (
                        "Loading allocated PMC..."
                      ) : allocatedPmc.pmcId ? (
                        <>
                          <b>{allocatedPmc.pmcName}</b>
                          {allocatedPmc.pmcMobile && (
                            <>
                              <br />
                              Mobile:{" "}
                              <a
                                className="font-bold text-emerald-700 hover:underline"
                                href={`tel:${allocatedPmc.pmcMobile}`}
                              >
                                {allocatedPmc.pmcMobile}
                              </a>
                            </>
                          )}
                          {allocatedPmc.pmcEmail && (
                            <>
                              <br />
                              Email:{" "}
                              <a
                                className="font-bold text-emerald-700 hover:underline"
                                href={`mailto:${allocatedPmc.pmcEmail}`}
                              >
                                {allocatedPmc.pmcEmail}
                              </a>
                            </>
                          )}
                          {allocatedPmc.pmcAddress && (
                            <>
                              <br />
                              Office: {allocatedPmc.pmcAddress}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <b>PMC not allocated yet.</b>
                          <br />
                          State Branch allocation ke baad yahin authorised PMC
                          ka contact automatically dikhega.
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[11px] text-emerald-800">
                    <span className="font-black">● LIVE FIRESTORE SYNC</span>
                    <span>
                      {liveSyncAt ? dayjs(liveSyncAt).format("hh:mm:ss A") : "—"}
                    </span>
                  </div>

                  {lastAppId && (
                    <div className="mt-5 rounded-3xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-800">
                        Last Submitted
                      </div>

                      <div className="mt-2 font-mono text-sm font-black text-slate-900">
                        {lastAppId}
                      </div>

                      <div className="text-xs text-slate-700">
                        {lastAppDate
                          ? dayjs(lastAppDate).format("DD/MM/YYYY, hh:mm A")
                          : ""}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-5 rounded-[34px] border border-white bg-white/95 p-6 shadow-[0_24px_60px_rgba(6,29,69,0.10)]">
                  <h2 className="text-lg font-black text-slate-950">
                    Application Status Check
                  </h2>

                  <form onSubmit={handleStatusCheck} className="mt-4 grid gap-4">
                    <div>
                      <Label>Application ID</Label>
                      <Input
                        value={checkId}
                        onChange={setCheckId}
                        placeholder="NAFED/2526/Godown/0001"
                      />
                    </div>

                    <div>
                      <Label>Registered Mobile Number</Label>
                      <Input
                        value={checkMobile}
                        onChange={(v) =>
                          setCheckMobile(v.replace(/\D/g, "").slice(0, 10))
                        }
                        placeholder="10-digit mobile number"
                      />
                    </div>

                    <button
                      disabled={checking}
                      className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-black text-white hover:bg-slate-800"
                    >
                      {checking ? "Checking..." : "Check Status"}
                    </button>
                  </form>

                  <div className="mt-4">{statusBadge(checkResult)}</div>

                  {checkResult && (
                    <div className="mt-4 grid gap-2">
                      {renderStatusLine("Institution", checkResult.pacsName)}
                      {renderStatusLine("State", checkResult.state)}
                      {renderStatusLine("District", checkResult.district)}
                      {renderStatusLine("Taluka", checkResult.taluka)}
                      {renderStatusLine("Village", checkResult.village)}
                      {renderStatusLine("Status", checkResult.status)}
                      {renderStatusLine("PMC", checkResult.allocatedPmcName)}
                      {renderStatusLine("Site Visit", checkResult.siteVisitStatus)}
                      {renderStatusLine("PMC Decision", checkResult.pmcDecision)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Godown Project Online Application Form"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <PremiumCard
            title="Application Source"
            subtitle="Select how this application is being submitted."
          >
            <div className="grid md:grid-cols-3 gap-4">
              {visibleApplicationSources.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setApplicationSource(item.value)}
                  className={cx(
                    "rounded-3xl border p-5 text-left transition-all",
                    applicationSource === item.value
                      ? "border-emerald-500 bg-emerald-50 shadow-lg"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  )}
                >
                  <div className="text-sm font-black text-slate-900">
                    {item.label}
                  </div>
                  <div className="mt-2 text-xs text-slate-500 leading-6">
                    {item.value === "PACS_DIRECT" &&
                      "Application submitted directly by PACS / Society."}
                    {item.value === "PMC_ASSISTED" &&
                      "Application filled with assistance of PMC / authorized team."}
                    {item.value === "SHINE_AGRI_SELF" &&
                      "Restricted internal option for Shine Agri self project only."}
                  </div>
                </button>
              ))}
            </div>
          </PremiumCard>

          <PremiumCard title="Institution Details">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Institution Name</Label>
                <Input
                  value={pacsName}
                  onChange={setPacsName}
                  placeholder="Example: XYZ Seva Sahakari Mandali Ltd."
                />
              </div>

              <div>
                <Label>Registration Number</Label>
                <Input
                  value={pacsRegNo}
                  onChange={setPacsRegNo}
                  placeholder="Registration number"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Type of Institution</Label>
                <Select value={pacsType} onChange={setPacsType}>
                  <option value="PACS">PACS</option>
                  <option value="MSCS">Multi-State Cooperative Society</option>
                  <option value="FPC">Farmer Producer Company</option>
                  <option value="OTHER">Other Eligible Institution</option>
                </Select>
              </div>
            </div>
          </PremiumCard>

          <PremiumCard title="Contact Person">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Contact Person Name</Label>
                <Input
                  value={contactName}
                  onChange={setContactName}
                  placeholder="Chairman / CEO / Secretary"
                />
              </div>

              <div>
                <Label>Mobile Number</Label>
                <Input
                  value={contactPhone}
                  onChange={(v) =>
                    setContactPhone(v.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="10-digit mobile"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Invitation Code / Reference Code</Label>
                <Input
                  value={invitationCode}
                  onChange={(v) => setInvitationCode(v.trim())}
                  placeholder="Optional"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  value={contactEmail}
                  onChange={setContactEmail}
                  placeholder="example@email.com"
                />
              </div>
            </div>
          </PremiumCard>

          <PremiumCard
            title="Location & PMC Allocation"
            subtitle="Only the PMC officially allocated to this PACS by State Branch is displayed."
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <Select
                  value={state}
                  onChange={(value) => {
                    setState(value);
                    setDistrict("");
                    setTaluka("");
                  }}
                >
                  {ALL_INDIA_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label>District</Label>
                <Select
                  value={district}
                  onChange={(v) => {
                    setDistrict(v);
                    setTaluka("");
                  }}
                >
                  <option value="">Select District</option>
                  {districtOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Taluka / Block</Label>
                <Input
                  value={taluka}
                  onChange={setTaluka}
                  placeholder="Enter Taluka / Block manually"
                />
              </div>

              <div>
                <Label>Village / Location</Label>
                <Input
                  value={village}
                  onChange={setVillage}
                  placeholder="Village / location name"
                />
              </div>
            </div>

            <div className="rounded-3xl border border-blue-200 bg-blue-50 p-5">
              <div className="text-[11px] font-black uppercase tracking-[0.16em] text-blue-800">
                Allocated PMC
              </div>

              <div className="mt-2 text-lg font-black text-slate-900">
                {pmcLoading ? "Checking PMC allocation..." : allocatedPmc.pmcName}
              </div>

              <div className="mt-1 text-sm text-slate-600">
                {allocatedPmc.pmcMobile && <>Mobile: {allocatedPmc.pmcMobile} </>}
                {allocatedPmc.pmcEmail && <> | Email: {allocatedPmc.pmcEmail}</>}
              </div>

              <div className="mt-2 text-xs text-slate-500 leading-6">
                PMC allocation is read from your approved PACS profile. A
                district-level default or another PMC contact is never shown here.
              </div>
            </div>
          </PremiumCard>

          <PremiumCard title="Proposed Godown Details">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Capacity</Label>
                <Select
                  value={capacityMt}
                  onChange={(v) => {
                    setCapacityMt(v);
                    if (v) setGodownType(`${v} MT Godown`);
                  }}
                >
                  <option value="">Select Capacity</option>
                  {CAPACITY_OPTIONS.map((mt) => (
                    <option key={mt} value={String(mt)}>
                      {mt} MT
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label>Godown Type</Label>
                <Select value={godownType} onChange={setGodownType}>
                  {CAPACITY_OPTIONS.map((mt) => (
                    <option key={mt} value={`${mt} MT Godown`}>
                      {mt} MT Godown
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Plot Length</Label>
                <Input
                  type="number"
                  value={plotLength}
                  onChange={setPlotLength}
                  placeholder="Length in feet / meter"
                />
              </div>

              <div>
                <Label>Plot Width</Label>
                <Input
                  type="number"
                  value={plotWidth}
                  onChange={setPlotWidth}
                  placeholder="Width in feet / meter"
                />
              </div>
            </div>

            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-sm font-black text-slate-900">
                Calculated Plot Area: {landArea.toLocaleString("en-IN")}
              </div>
            </div>

            <div>
              <Label>Land Details / Survey Number</Label>
              <Input
                value={landDetails}
                onChange={setLandDetails}
                placeholder="Survey number, land ownership / lease details"
              />
            </div>

            <div>
              <Label>Google Map Link</Label>
              <Input
                value={mapUrl}
                onChange={setMapUrl}
                placeholder="Optional map link"
              />
            </div>
          </PremiumCard>

          <PremiumCard
            title="Financial Strength Details"
            subtitle="These details will be printed in the application PDF annexure."
          >
            <div className="grid gap-3">
              {profitRows.map((row, index) => (
                <div
                  key={`profit-${index}`}
                  className="grid md:grid-cols-2 gap-3 rounded-3xl border border-slate-200 bg-white p-4"
                >
                  <div>
                    <Label>Financial Year</Label>
                    <Select
                      value={row.fy}
                      onChange={(v) => updateProfitRow(index, "fy", v)}
                    >
                      <option value="">Select FY</option>
                      {financialYearOptions.map((fy) => (
                        <option key={fy.value} value={fy.value}>
                          {fy.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Label>Net Profit Amount</Label>
                    <Input
                      type="number"
                      value={row.profit}
                      onChange={(v) => updateProfitRow(index, "profit", v)}
                      placeholder="Enter amount. Use negative for loss."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Reserve Fund</Label>
                <Input
                  type="number"
                  value={reserveFund}
                  onChange={setReserveFund}
                  placeholder="Amount"
                />
              </div>

              <div>
                <Label>Building / Makan Fund</Label>
                <Input
                  type="number"
                  value={buildingFund}
                  onChange={setBuildingFund}
                  placeholder="Amount"
                />
              </div>

              <div>
                <Label>Share Capital</Label>
                <Input
                  type="number"
                  value={shareCapital}
                  onChange={setShareCapital}
                  placeholder="Amount"
                />
              </div>

              <div>
                <Label>Audit Class</Label>
                <Select value={auditClass} onChange={setAuditClass}>
                  <option value="">Select Audit Class</option>
                  <option value="A">A Class</option>
                  <option value="B">B Class</option>
                  <option value="C">C Class</option>
                  <option value="D">D Class</option>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="text-[11px] font-black text-emerald-800 uppercase tracking-wider">
                  Total Profit
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(totalProfit)}
                </div>
              </div>

              <div className="rounded-3xl border border-blue-100 bg-blue-50 p-4">
                <div className="text-[11px] font-black text-blue-800 uppercase tracking-wider">
                  Average Profit
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(averageProfit)}
                </div>
              </div>

              <div className="rounded-3xl border border-amber-100 bg-amber-50 p-4">
                <div className="text-[11px] font-black text-amber-800 uppercase tracking-wider">
                  Fund Strength
                </div>
                <div className="mt-1 text-lg font-black text-slate-900">
                  {formatInr(totalFunds)}
                </div>
              </div>
            </div>
          </PremiumCard>

          <PremiumCard title="Upload Supporting Documents">
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["Registration Certificate", regFiles, setRegFiles],
                ["Land Documents", landFiles, setLandFiles],
                ["Board / Managing Committee Resolution", committeeFiles, setCommitteeFiles],
                ["Last 3 Years Audit Reports", auditFiles, setAuditFiles],
                ["Other Supporting Documents", otherFiles, setOtherFiles],
              ].map(([title, files, setter], index) => (
                <div
                  key={title}
                  className={cx(
                    "rounded-3xl border border-slate-200 bg-white p-4",
                    index === 4 && "md:col-span-2"
                  )}
                >
                  <Label>{title}</Label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setter(Array.from(e.target.files || []))}
                    className="mt-2 w-full text-xs file:mr-2 file:rounded-full file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-emerald-700"
                  />
                  {files.length > 0 && (
                    <div className="mt-2 text-[11px] text-slate-600">
                      {files.length} file(s) selected.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </PremiumCard>

          <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-600 leading-6">
              After submission, application PDF will download automatically and
              the application will be routed to the relevant workflow.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                disabled={submitting}
                className={cx(
                  "rounded-full px-6 py-2.5 text-sm font-black text-white shadow-lg transition-all duration-150",
                  submitting
                    ? "bg-emerald-400/80 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700 hover:shadow-[0_18px_40px_rgba(16,185,129,0.35)]"
                )}
              >
                {submitting ? "Submitting..." : "Submit & Download PDF"}
              </button>
            </div>
          </div>
        </form>
      </FullScreenModal>
    </>
  );
}
